import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter, MapPin, Search, Wrench, X } from "lucide-react";

import { useAssets } from "@/api/queries";
import type { Asset } from "@/api/types";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { Input, Select } from "@/components/Input";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/layout/PageHeader";
import { Page } from "@/layout/Page";
import {
  formatAssetCondition,
  formatAssetLifecycle,
  formatDate,
} from "@/lib/format";
import { assetTone } from "@/lib/stock";

const PAGE_SIZE = 50;

const LIFECYCLES = [
  "IN_SERVICE",
  "IN_REPAIR",
  "OUT_OF_SERVICE",
  "LOST",
  "DISCARDED",
  "SOLD",
];

/**
 * The asset table (PHASE 7 frontend).
 *
 * One row per physical object, not per catalogue entry. Availability comes from
 * the server: a worn drill is still usable, a pristine one awaiting calibration
 * is not, and the client does not get to decide which.
 */
export function AssetsPage() {
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(term);

  const lifecycle = params.get("lifecycle") ?? undefined;
  const availableOnly = params.get("available") === "1";
  const offset = Number(params.get("offset") ?? 0);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.trim()), 220);
    return () => window.clearTimeout(timer);
  }, [term]);

  const query = useMemo(
    () => ({
      q: debounced || undefined,
      lifecycle: lifecycle as never,
      available_only: availableOnly,
      limit: PAGE_SIZE,
      offset,
    }),
    [debounced, lifecycle, availableOnly, offset],
  );

  const { data, isLoading, error, refetch } = useAssets(query);

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  const hasFilters = Boolean(debounced || lifecycle || availableOnly);

  return (
    <Page>
      <PageHeader
        title="Outils / Assets"
        subtitle="Chaque ligne est un objet physique. Un deuxième exemplaire d'un produit est un asset, pas un deuxième produit."
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                update({ q: event.target.value, offset: undefined });
              }}
              placeholder="Code, numéro de série, nom…"
              className="pl-9"
              aria-label="Rechercher un outil"
            />
          </div>

          <Select
            value={lifecycle ?? ""}
            onChange={(event) => update({ lifecycle: event.target.value || undefined })}
            aria-label="Filtrer par statut"
            className="sm:w-52"
          >
            <option value="">Tous les statuts</option>
            {LIFECYCLES.map((value) => (
              <option key={value} value={value}>
                {formatAssetLifecycle(value)}
              </option>
            ))}
          </Select>

          <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-border bg-surface-sunken px-3 text-[13px] text-ink-muted">
            <input
              type="checkbox"
              checked={availableOnly}
              onChange={(event) => update({ available: event.target.checked ? "1" : undefined })}
              className="accent-[#f2661f]"
            />
            Disponibles
          </label>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setTerm("");
                setParams(new URLSearchParams(), { replace: true });
              }}
              className="flex h-10 items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-subtle hover:text-ink"
            >
              <X className="size-4" aria-hidden />
              Effacer
            </button>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={10} columns={7} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={hasFilters ? <Filter className="size-6" aria-hidden /> : <Wrench className="size-6" aria-hidden />}
              title={hasFilters ? "Aucun outil ne correspond" : "Aucun outil enregistré"}
              description={
                hasFilters
                  ? "Essayez un autre terme, ou retirez les filtres."
                  : "Ajoutez un produit au catalogue, puis enregistrez les exemplaires physiques que vous possédez."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-16">Photo</Th>
                      <Th className="w-32 whitespace-nowrap">Code</Th>
                      <Th className="max-w-0">Produit</Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">N° de série</Th>
                      <Th className="hidden whitespace-nowrap @3xl:table-cell">État</Th>
                      <Th>Statut</Th>
                      <Th className="max-w-0">Emplacement</Th>
                      {/* Measured with scripts/table-min-widths.mjs, container px:
                          6 cols ~ 594 -> @3xl · 7 cols ~ 809 -> @4xl
                          8 cols ~ 941 -> @5xl · 9 cols ~1106 -> @6xl
                          Emprunteur sat at @4xl (896) while 8 columns need 941,
                          which overflowed in a band no tested width covered. */}
                      <Th className="hidden whitespace-nowrap @5xl:table-cell">Emprunteur</Th>
                      <Th className="hidden whitespace-nowrap @6xl:table-cell">
                        Dernière inspection
                      </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((asset) => (
                      <AssetRow key={asset.id} asset={asset} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((asset) => (
                  <li key={asset.id}>
                    <AssetCard asset={asset} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && (
          <Pagination
            offset={data.offset}
            limit={data.limit}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>
    </Page>
  );
}

function AssetRow({ asset }: { asset: Asset }) {
  return (
    <Tr>
      <Td>
        <Photo mediaId={asset.media_id} alt={asset.product_name} className="size-10" />
      </Td>
      <Td className="whitespace-nowrap font-mono text-[12px]">
        <Link to={`/assets/${asset.id}`} className="text-ink hover:text-brand">
          {asset.asset_code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link
          to={`/assets/${asset.id}`}
          className="block truncate font-medium text-ink hover:text-brand"
        >
          {asset.name ?? asset.product_name}
        </Link>
        <p className="truncate text-[11px] text-ink-subtle">
          {[asset.product_manufacturer, asset.product_part_number].filter(Boolean).join(" · ")}
        </p>
      </Td>
      <Td className="hidden whitespace-nowrap font-mono text-[12px] text-ink-muted @4xl:table-cell">
        {asset.serial_number ?? "—"}
      </Td>
      <Td className="hidden whitespace-nowrap text-[13px] text-ink-muted @3xl:table-cell">
        {formatAssetCondition(asset.condition)}
      </Td>
      <Td>
        <AssetStatus asset={asset} />
      </Td>
      <Td className="max-w-0">
        {asset.place_label ? (
          <span className="flex items-center gap-1.5 text-[13px] text-ink-muted">
            <MapPin className="size-3.5 shrink-0 text-ink-subtle" aria-hidden />
            <span className="truncate">{asset.place_label}</span>
          </span>
        ) : (
          <span className="text-ink-subtle">—</span>
        )}
      </Td>
      <Td className="hidden whitespace-nowrap text-[13px] @5xl:table-cell">
        {asset.borrower_name ? (
          <span className={asset.loan_is_overdue ? "text-status-out" : "text-ink-muted"}>
            {asset.borrower_name}
            {asset.loan_due_on && (
              <span className="block text-[11px]">→ {formatDate(asset.loan_due_on)}</span>
            )}
          </span>
        ) : (
          <span className="text-ink-subtle">—</span>
        )}
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-subtle @6xl:table-cell">
        {formatDate(asset.last_inspected_at)}
      </Td>
    </Tr>
  );
}

/**
 * The one badge that says what is true of the tool right now.
 *
 * "Prêté" is neither a lifecycle nor a condition — a lent drill is in service
 * and in good shape, and still not something you can promise anyone. Showing
 * the lifecycle alone would say "En service" next to a tool Patrick has had for
 * a week.
 */
function AssetStatus({ asset, className }: { asset: Asset; className?: string }) {
  if (asset.borrower_name) {
    return (
      <StatusBadge tone={asset.loan_is_overdue ? "out" : "loaned"} className={className}>
        {asset.loan_is_overdue ? "En retard" : "Prêté"}
      </StatusBadge>
    );
  }
  return (
    <StatusBadge tone={assetTone(asset.lifecycle)} className={className}>
      {formatAssetLifecycle(asset.lifecycle)}
    </StatusBadge>
  );
}

function AssetCard({ asset }: { asset: Asset }) {
  return (
    <Link
      to={`/assets/${asset.id}`}
      className="flex items-center gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <Photo mediaId={asset.media_id} alt={asset.product_name} className="size-14 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">
          {asset.name ?? asset.product_name}
        </p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">
          {asset.asset_code}
          {asset.serial_number ? ` · ${asset.serial_number}` : ""}
        </p>
        <p className="mt-1 flex items-center gap-1 text-[12px] text-ink-muted">
          <MapPin className="size-3 shrink-0" aria-hidden />
          <span className="truncate">{asset.place_label ?? "Aucun emplacement"}</span>
        </p>
        {asset.borrower_name && (
          <p
            className={
              "truncate text-[12px] " +
              (asset.loan_is_overdue ? "text-status-out" : "text-ink-muted")
            }
          >
            Prêté à {asset.borrower_name}
          </p>
        )}
      </div>
      <AssetStatus asset={asset} className="shrink-0" />
    </Link>
  );
}
